/**
 * AccountContext module.
 * Provides context and state management for user account profile data.
 * Extracted from UserContext for focused re-render boundaries.
 */
// External packages
import { createContext, useContext, useReducer, useEffect, useMemo } from "react";

// Relative imports - Utils
import * as logoutChannel from "../shared/utils/events/logoutChannel";

const initialAccount = {
  id: "",
  personal_id: "",
  first_name: "",
  last_name: "",
  name: "",
  email: "",
  phone: "",
  company_id: "",
  company_name: "",
  type: "",
  invoice_method: "",
  invoice_detailed: "",
  debt: 0,
  collection_state_code: "",
  billing_address_street: "",
  billing_address_city: "",
  billing_address_postalcode: "",
  billing_address_apartment: "",
  billing_address_entrance: "",
  billing_address_floor: "",
  billing_address_neighborhood: "",
  billing_address_number: "",
  shipping_address_street: "",
  shipping_address_city: "",
  shipping_address_postalcode: "",
  shipping_address_apartment: "",
  shipping_address_entrance: "",
  shipping_address_floor: "",
  shipping_address_neighborhood: "",
  shipping_address_number: "",
};

const initialState = {
  account: initialAccount,
};

function reducer(state, action) {
  switch (action.type) {
    case "STORE_ACCOUNT_DATA":
      return {
        ...state,
        account: { ...state.account, ...action.payload },
      };
    case "SET_CARD_LAST4":
      return {
        ...state,
        account: { ...state.account, card_last4: action.payload },
      };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

const AccountContext = createContext(null);

/**
 * AccountProvider component.
 * Provides account profile state and actions to its children via context.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element} The provider with account context value.
 */
export function AccountProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Reset account state on logout.
  useEffect(() => {
    return logoutChannel.subscribe(() => dispatch({ type: "RESET" }));
  }, []);

  const storeAccountData = (p) =>
    dispatch({ type: "STORE_ACCOUNT_DATA", payload: p });

  const setCardLast4 = (last4) =>
    dispatch({ type: "SET_CARD_LAST4", payload: last4 });

  const value = useMemo(
    () => ({
      account: state.account,

      // Account-light aliases.
      firstName: state.account.first_name,
      lastName: state.account.last_name,
      personalId: state.account.personal_id,
      email: state.account.email,
      phone: state.account.phone,
      companyId: state.account.company_id,
      companyName: state.account.company_name,
      clientType: state.account.type,
      invoiceMethod: state.account.invoice_method,
      invoiceDetailed: state.account.invoice_detailed,

      // Billing address.
      billingAddressStreet: state.account.billing_address_street,
      billingAddressCity: state.account.billing_address_city,
      billingAddressPostalcode: state.account.billing_address_postalcode,
      billingAddressApartment: state.account.billing_address_apartment,
      billingAddressEntrance: state.account.billing_address_entrance,
      billingAddressFloor: state.account.billing_address_floor,
      billingAddressNeighborhood: state.account.billing_address_neighborhood,
      billingAddressNumber: state.account.billing_address_number,

      // Shipping address.
      shippingAddressStreet: state.account.shipping_address_street,
      shippingAddressCity: state.account.shipping_address_city,
      shippingAddressPostalcode: state.account.shipping_address_postalcode,
      shippingAddressApartment: state.account.shipping_address_apartment,
      shippingAddressEntrance: state.account.shipping_address_entrance,
      shippingAddressFloor: state.account.shipping_address_floor,
      shippingAddressNeighborhood: state.account.shipping_address_neighborhood,
      shippingAddressNumber: state.account.shipping_address_number,

      cardLast4: state.account.card_last4,

      // Actions.
      storeAccountData,
      setCardLast4,
    }),
    [state]
  );

  return (
    <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
  );
}

/**
 * Custom hook to access the AccountContext value.
 *
 * @returns {Object} Account context value.
 */
export function useAccountContext() {
  const context = useContext(AccountContext);
  if (context === undefined || context === null) {
    throw new Error(
      "useAccountContext must be used within an AccountProvider"
    );
  }
  return context;
}
